// Banner: people with no access level (unrestricted) → pick a level for each, review, apply.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { ACCESS_ROLE_LABELS } from "../../lib/access";

const LEVELS = ["operator", "manager", "finance", "view_only"];

export default function UnassignedLevels({ onApplied }) {
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/access/unassigned").then(({ data }) => { setRows(data.users); setPick(Object.fromEntries(data.users.map((u) => [u.id, u.proposed]))); }).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows?.length) return null;
  const apply = async () => {
    if (!window.confirm(`Set access levels for ${rows.length} people? They follow their level's preset from their next page load.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/access/assign-levels", rows.map((u) => ({ user_id: u.id, access_role: pick[u.id] })));
      toast.success(`${data.updated} ${data.updated === 1 ? "person" : "people"} updated`); load(); onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); } finally { setBusy(false); }
  };
  return (
    <div className="card-dark p-4 mb-4 border border-amber-400/30" data-testid="unassigned-levels">
      <div className="flex items-start gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5" />
        <div><div className="text-sm font-semibold text-white">{rows.length} people have no access level</div>
          <div className="text-[12px] text-slate-400">They're treated as unrestricted, so the presets don't apply to them. Choose a level for each and apply.</div></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mb-3 max-h-[280px] overflow-y-auto pr-1">
        {rows.map((u) => (
          <div key={u.id} className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2" data-testid={`unassigned-${u.id}`}>
            <div className="min-w-0 flex-1"><div className="text-[12.5px] text-white truncate">{u.name || u.email}</div><div className="text-[10.5px] text-slate-500 font-mono truncate">{u.email}</div></div>
            <select value={pick[u.id]} onChange={(e) => setPick({ ...pick, [u.id]: e.target.value })} className="input-dark !w-32 shrink-0 !py-1 text-[12px]" data-testid={`unassigned-level-${u.id}`}>
              {LEVELS.map((l) => <option key={l} value={l}>{ACCESS_ROLE_LABELS[l]}</option>)}
            </select>
          </div>))}
      </div>
      <button type="button" onClick={apply} disabled={busy} className="btn-coral disabled:opacity-50" data-testid="unassigned-apply">{busy ? "Applying…" : "Apply levels"}</button>
    </div>
  );
}
